import { useEffect, useRef, useState } from 'react';
import { buildSetEvents, buildRestEvents, buildStationRestEvents, setupText, stationSetupText, resolveTempo, exerciseName, doneText, speechSecs, LEAD_SECS } from '@/lib/coachScript';

const sorted = (r) => ({ ...r, events: r.events.sort((a, b) => a.t - b.t) });
const targetOf = (it) => it.target_reps || parseInt(it.reps, 10) || 10;

// Strength: one plan step per exercise (with sets). mode = 'continuous' | 'manual' — only progression differs.
export function buildStrengthPlan(items, exMap, { motivation, mode, rate }) {
  const continuous = mode === 'continuous';
  const base = items.map((it, i) => {
    const ex = exMap[it.exercise_id];
    const tempo = resolveTempo(it, ex);
    const target = targetOf(it);
    const sets = Math.max(1, parseInt(it.sets, 10) || 1);
    const name = exerciseName(it, ex);
    const setEvents = Array.from({ length: sets }, (_, s) => sorted(buildSetEvents(tempo, target, { first: s === 0, motivation, name })));
    const setup = setupText({ ...it, target_reps: target }, ex, { first: i === 0, tempo, continuous });
    return { idx: i, item: it, ex, name, tempo, target, sets, rest: Math.max(0, it.rest_seconds || 0), setup, setupSecs: speechSecs(setup, rate), setEvents };
  });
  base.forEach((p, i) => {
    const nx = base[i + 1];
    p.restEvents = Array.from({ length: p.sets }, (_, s) => buildRestEvents(p.rest, {
      lastSet: s === p.sets - 1, name: p.name, nextName: nx?.name, nextSetup: continuous && s === p.sets - 1 && nx ? nx.setup : null,
    }));
  });
  return { kind: 'strength', continuous, exercises: base, done: doneText('strength') };
}

// HIIT / Circuit / Fitness Racing: one plan step per station occurrence (round × exercise), always continuous.
// Recovery after a station = its programmed rest; after the last station of a round = the round rest only.
export function buildCircuitPlan(workout, exMap, { motivation, race, rate }) {
  const items = workout.items || [];
  const rounds = Math.max(1, workout.rounds || 1);
  const roundRest = Math.max(0, workout.rest_between_rounds_seconds || 0);
  const transition = (it) => (workout.transition_seconds != null && workout.transition_seconds !== ''
    ? Math.max(0, Number(workout.transition_seconds)) : (it.rest_seconds > 0 ? it.rest_seconds : 20));
  const steps = [];
  for (let r = 1; r <= rounds; r++) {
    items.forEach((it, i) => {
      const ex = exMap[it.exercise_id];
      const tempo = resolveTempo(it, ex, { circuit: true });
      const name = exerciseName(it, ex);
      const lastInRound = i === items.length - 1;
      const rest = lastInRound ? (r < rounds ? roundRest : 0) : race ? transition(it) : Math.max(0, it.rest_seconds || 0);
      const setup = stationSetupText(it, ex, { first: r === 1 && i === 0, round: r, station: i, race });
      steps.push({ idx: i, item: it, ex, name, tempo, target: it.target_reps || null, sets: 1, rest, round: r, rounds, station: i, stations: items.length,
        lastInRound, setup, setupSecs: speechSecs(setup, rate), setEvents: [sorted(buildSetEvents(tempo, it.target_reps, { first: true, motivation, name, style: 'circuit' }))] });
    });
  }
  steps.forEach((p, k) => {
    p.restEvents = [buildStationRestEvents(p.rest, { race, roundEnd: p.lastInRound, round: p.round, nextSetup: steps[k + 1]?.setup })];
  });
  return { kind: race ? 'race' : 'circuit', continuous: true, exercises: steps, rounds, done: doneText(race ? 'race' : 'circuit') };
}

export const planTexts = (p) => [p.setup, ...p.setEvents.flatMap(s => s.events.flatMap(e => [e.text, e.plain])), ...p.restEvents.flatMap(r => r.map(e => e.text))];

/** The workout timer. It alone decides when reps, sets, rests and transitions happen; the coach only speaks on its events. */
export function useGuidedEngine({ plan, coach, settings }) {
  const ex = plan.exercises;
  const [st, setSt] = useState({ phase: 'intro', ex: 0, set: 0 });
  const [el, setEl] = useState(0);
  const [paused, setPaused] = useState(false);
  const [extra, setExtra] = useState(0);
  const [setsDone, setSetsDone] = useState(() => ex.map(() => 0));
  const [durationSec, setDurationSec] = useState(0);
  const t0 = useRef(0), ptr = useRef(0), pauseAt = useRef(0), startAt = useRef(0), pausedTotal = useRef(0);
  const sRef = useRef(settings); sRef.current = settings;
  const cur = ex[st.ex];
  const auto = plan.continuous;
  const spec = st.phase === 'work' ? cur.setEvents[st.set]
    : st.phase === 'rest' ? { events: cur.restEvents[st.set], duration: cur.rest + extra }
      : st.phase === 'setup' && auto ? { events: [{ t: 0, text: cur.setup, mode: 'queue' }], duration: cur.setupSecs + extra } : null;

  const enter = (next) => {
    ptr.current = 0; t0.current = performance.now(); setEl(0); setExtra(0); setPaused(false); setSt(next);
    if (next.phase === 'done') setDurationSec(Math.round((Date.now() - startAt.current - pausedTotal.current) / 1000));
  };
  const lastEx = st.ex + 1 >= ex.length;
  // Next exercise: continuous goes straight to its count-in (instructions were given during the rest);
  // manual (and skips) show the setup screen first.
  const goNext = (fromRest) => {
    if (lastEx) return enter({ phase: 'done', ex: st.ex, set: st.set });
    if (auto && fromRest) return enter({ phase: 'work', ex: st.ex + 1, set: 0 });
    return enter({ phase: 'setup', ex: st.ex + 1, set: 0 });
  };

  const finishPhase = () => {
    const lastSet = st.set + 1 >= cur.sets;
    if (st.phase === 'setup') return enter({ phase: 'work', ex: st.ex, set: 0 });
    if (st.phase === 'work') {
      setSetsDone(d => d.map((v, i) => (i === st.ex ? v + 1 : v)));
      if (lastSet && lastEx) return enter({ phase: 'done', ex: st.ex, set: st.set });
      if (cur.rest > 0) return enter({ phase: 'rest', ex: st.ex, set: st.set });
      return lastSet ? goNext(plan.kind !== 'strength') : enter({ phase: 'work', ex: st.ex, set: st.set + 1 });
    }
    return lastSet ? goNext(true) : enter({ phase: 'work', ex: st.ex, set: st.set + 1 });
  };
  const finishRef = useRef(finishPhase); finishRef.current = finishPhase;

  useEffect(() => {
    if (st.phase === 'setup' && !auto) coach.say(cur.setup, 'queue');
    if (st.phase === 'done') coach.say(plan.done, 'queue');
  }, [st.phase, st.ex]);

  useEffect(() => {
    if (!spec || paused) return undefined;
    const id = setInterval(() => {
      const t = (performance.now() - t0.current) / 1000;
      const evs = spec.events;
      while (ptr.current < evs.length && evs[ptr.current].t + (evs[ptr.current].shift ? extra : 0) <= t) {
        const e = evs[ptr.current++];
        const s = sRef.current;
        if (e.opt && !s.restAnnouncements && st.phase === 'rest') continue;
        if (e.opt && !s.motivation && st.phase === 'work') continue;
        coach.say(!s.motivation && e.plain ? e.plain : e.text, e.mode);
      }
      setEl(t);
      // Never cut the coach off mid-instructions: hold the count-in (not the rep tempo) until the speech ends, max 15s.
      if (t >= spec.duration && st.phase === 'rest' && sRef.current.enabled && coach.busy && t < spec.duration + 15) return;
      if (t >= spec.duration) { clearInterval(id); finishRef.current(); }
    }, 50);
    return () => clearInterval(id);
  }, [st, paused, extra]);

  const togglePause = () => {
    if (!paused) { pauseAt.current = performance.now(); coach.pause(); }
    else { const d = performance.now() - pauseAt.current; t0.current += d; pausedTotal.current += d; coach.resume(); }
    setPaused(p => !p);
  };

  return {
    st, el, paused, setsDone, durationSec, cur, spec, extra, auto, kind: plan.kind,
    begin: () => { startAt.current = Date.now(); coach.unlock(); enter({ phase: 'setup', ex: 0, set: 0 }); },
    ready: () => { coach.stop(); enter({ phase: 'work', ex: st.ex, set: 0 }); },
    repeat: () => coach.say(cur.setup, 'interrupt'),
    skipExercise: () => { coach.stop(); goNext(false); },
    // Skipping a rest/transition only ends the countdown; finishPhase picks the correct next set/station/round.
    skipRest: () => { coach.stop(); finishPhase(); },
    stationDone: () => finishPhase(),
    addRest: (s) => setExtra(x => x + s),
    togglePause,
    stop: () => { coach.stop(); enter({ phase: 'done', ex: st.ex, set: st.set }); },
  };
}

// What the client should be doing right now inside a set (purely derived from the timer).
export function workDisplay(cur, el, duration) {
  if (el < LEAD_SECS) return { lead: true, count: Math.ceil(LEAD_SECS - el) };
  const t = el - LEAD_SECS;
  if (cur.tempo.kind === 'open') return { open: true, elapsed: t };
  if (cur.tempo.kind === 'timed') return { timed: true, remaining: Math.max(0, Math.ceil(duration - el)), progress: Math.min(1, t / cur.target) };
  const repSecs = cur.tempo.repSecs || 2;
  const rep = Math.min(cur.target, Math.floor(t / repSecs) + 1);
  let within = t - (rep - 1) * repSecs;
  if (cur.tempo.kind === 'ballistic') return { rep, cue: 'Go', progress: Math.min(1, within / repSecs) };
  for (const p of cur.tempo.phases) {
    if (within < p.secs || p === cur.tempo.phases[cur.tempo.phases.length - 1]) {
      return { rep, cue: p.cue || 'Reset', progress: Math.min(1, within / p.secs), second: Math.min(p.secs, Math.floor(within) + 1), secs: p.secs };
    }
    within -= p.secs;
  }
  return { rep };
}
